import React, { useEffect, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { graphql, useStaticQuery } from 'gatsby';
import { Button, classNames, Icon, List } from '@pella/sash-framework';
import { cdnQuery } from '../../utilities/cdnQuery';
import Image from '../image';
import { isValidArray } from '../../utilities/validations';
import FifteenMinuteConsultation from './consultation';
import FifteenMinuteLocationChange from './location-change';
import FifteenMinuteApptScheduled from './scheduled';

const FifteenMinuteAppt = ({ view, update15MinParent, canHaveAppt, branchID, externalCalendarTrigger }) => {
	const { professional } = useStaticQuery(query).contentstackConversionConfig;
	const { alternate_headings, learn_about } = professional;

	const [salesBio, setSalesBio] = useState({});
	const [showCalendar, setShowCalendar] = useState(false);
	const [consultationState, setConsultationState] = useState(null);

	const onShowCalendar = (value) => {
		setShowCalendar(value);
		update15MinParent(value);
	};

	useEffect(() => {
		if (externalCalendarTrigger) {
			setShowCalendar(true);
		}
	}, [externalCalendarTrigger]);

	useEffect(() => {
		if (showCalendar) {
			setTimeout(() => {
				const element = document.getElementById('salesPersonCard');
				if (element) {
					element.scrollIntoView({ behavior: 'smooth', block: 'start' });
				}
			}, 200);
		}
	}, [showCalendar]);

	useEffect(() => {
		fetchBioFromContentstack(branchID), [branchID];
	}, [branchID]);

	const fetchBioFromContentstack = (branchID) => {
		const query = `{
			"inhome": { "$eq": true },
			"$or": [
				{
					"branch_data": {
						"$in_query": {
							"branch_retail": {
								"$in": ${JSON.stringify([branchID])}
							}
						}
					}
				},
				{
					"branch_data": {
						"$in_query": {
							"branch_trade": {
								"$in": ${JSON.stringify([branchID])}
							}
						}
					}
				}
			]
		}`;

		cdnQuery('author', query, 'include[]=author_bio&include[]=image')
			.then((response) => setSalesBio(response.data.entries[0]))
			.catch(() => {});
	};

	const [descriptionExpanded, setDescriptionExpanded] = useState(false);
	const toggleDescription = () => setDescriptionExpanded(!descriptionExpanded);

	const [canTruncate, setCanTruncate] = useState(false);
	const measuredRef = useCallback((node) => {
		if (node) {
			const elHeight = node.scrollHeight;
			const computedStyles = window.getComputedStyle(node);
			const lineHeightPx = computedStyles.getPropertyValue('line-height');
			const lineHeight = parseInt(lineHeightPx.replace('px', ''), 10);
			const lineCount = elHeight / lineHeight;

			setCanTruncate(lineCount > 2);
		}
	}, []);

	const shouldClamp = canTruncate && !descriptionExpanded;

	const handleConsultationStateChange = (newState) => {
		setConsultationState(newState);
	};

	const salesPersonCardHeading = alternate_headings?.find((heading) => heading.heading_type === view).heading;
	const salesPersonCardSubheading = alternate_headings?.find((heading) => heading.heading_type === view)?.subheading;

	const fifteenMinuteAppointmentClasses = classNames({
		'mb-6 border border-gray-42 bg-white md:mb-8 md:mt-2 xl:mb-10 xl:mt-4 px-5 py-6 md:p-6': true,
		'w-full': view === 'projects',
		'w-fit': view === 'rta',
		'mx-auto lg:!w-[958px]': view === 'rta' && showCalendar,
	});

	const salesPersonCardClasses = classNames({
		'grid items-start md:grid-cols-[248px_1fr] md:grid-rows-[auto_auto_1fr] md:gap-x-8 gap-y-4': true,
		'mx-auto max-w-[714px]': view === 'rta',
	});

	const bioHeadingClasses = classNames({
		'text-h3 m-0 text-center md:col-start-2': true,
		'md:text-left': view === 'rta',
	});

	const bioImageClasses = classNames({
		'h-[108px] w-[108px] rounded-full md:h-[132px] md:w-[132px] mb-3 grid place-items-center bg-gray-15': true,
	});

	const bioNameClasses = classNames({
		'text-subtitle font-bold text-center': true,
		'min-h-4 min-w-[10ch] bg-gray-15 md:min-h-[18px] xl:min-h-5': !canHaveAppt,
	});

	const bioDummyLineClasses = classNames({
		'mt-1 min-h-3 min-w-[18ch] bg-gray-15 xl:min-h-3.5': true,
	});

	const bioDescriptionClasses = classNames({
		'text-body-2 text-center text-black line-clamp-none': true,
		'md:!text-left': view === 'rta',
		'!line-clamp-2': shouldClamp,
	});

	if (consultationState?.status == 'SUCCESS') {
		return (
			<FifteenMinuteApptScheduled
				consultationState={consultationState}
				salesBio={salesBio}
				view={view}
			/>
		);
	}

	return (
		<div
			id={`15MinAppt-${view}`}
			className={fifteenMinuteAppointmentClasses}
		>
			<div
				id='salesPersonCard'
				className={salesPersonCardClasses}
			>
				<h2 className={bioHeadingClasses}>{salesPersonCardHeading}</h2>
				<div
					id='salesPersonCard-bio'
					className='flex flex-col items-center md:row-span-3 md:row-start-1'
				>
					{canHaveAppt ? (
						<Image
							data={salesBio?.image?.[0]}
							params='?format=pjpg&auto=webp&quality=90'
							className={bioImageClasses}
						/>
					) : (
						<div className={bioImageClasses}>
							<Icon
								name='person'
								className='text-white'
								size={75}
							/>
						</div>
					)}

					<div>
						<p className={bioNameClasses}>{salesBio?.in_home_bio?.author_name}</p>
						{canHaveAppt ? (
							<div
								className='text-body-2 mt-1 max-w-[25ch] text-center text-black'
								dangerouslySetInnerHTML={{ __html: salesBio?.in_home_bio?.job_title }}
							/>
						) : (
							<>
								<div className={bioDummyLineClasses} />
								<div className={bioDummyLineClasses} />
							</>
						)}
					</div>

					{!showCalendar && (
						<Button
							color='primary-alt'
							onClick={() => onShowCalendar(true)}
							className='mt-3 flex items-center md:!px-[10px]'
						>
							<Icon
								size={20}
								name='calendar_month'
							/>

							<span>Schedule A 15 Min Call</span>
						</Button>
					)}

					{showCalendar && (
						<FifteenMinuteLocationChange
							canHaveAppt={canHaveAppt}
							rep={salesBio?.in_home_bio}
						/>
					)}
				</div>

				<div
					id='salesPersonCard-description'
					className='flex flex-col'
				>
					<p
						ref={measuredRef}
						className={`${bioDescriptionClasses} `}
					>
						{salesPersonCardSubheading || salesBio?.in_home_bio?.description}
					</p>

					{canTruncate && (
						<Button
							color='tertiary-alt'
							onClick={toggleDescription}
						>
							{`Show ${descriptionExpanded ? 'Less' : 'More'}`}
						</Button>
					)}
				</div>

				<div
					id='salesPersonCard-learn'
					className='w-full self-end bg-gray-5 px-3 pb-5 pt-4'
				>
					<p className='text-subtitle-2 mb-5 text-center'>{learn_about.heading}</p>
					{isValidArray(learn_about.group) && (
						<List
							bullets='none'
							className='grid grid-cols-2 gap-5 sm:grid-cols-4'
						>
							{learn_about.group.map((item, index) => (
								<li
									key={index}
									className='flex flex-col items-center'
								>
									<Icon
										name={item.material_symbol}
										size={32}
										weight={700}
										className='mb-3 text-gray-60'
									/>
									<p className='max-w-[15ch] text-center text-xs leading-[1.3] text-black'>{item.icon_heading}</p>
								</li>
							))}
						</List>
					)}
				</div>
			</div>

			{showCalendar && (
				<FifteenMinuteConsultation
					onConsultationStateChange={handleConsultationStateChange}
					rep={salesBio?.in_home_bio}
					view={view}
					canHaveAppt={canHaveAppt}
				/>
			)}
		</div>
	);
};

FifteenMinuteAppt.propTypes = {
	view: PropTypes.string.isRequired,
	update15MinParent: PropTypes.func,
	canHaveAppt: PropTypes.bool,
	branchID: PropTypes.string,
	externalCalendarTrigger: PropTypes.bool,
};

export default FifteenMinuteAppt;

export const query = graphql`
	query {
		contentstackConversionConfig {
			professional {
				alternate_headings {
					heading
					heading_type
					subheading
				}
				learn_about {
					heading
					group {
						icon_heading
						material_symbol
					}
				}
			}
		}
	}
`;
